import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  brandSchema,
  brandsPageSchema,
  countriesSchema,
  createBrandMultipartInit,
  serializeBrandsFilter,
  type BrandPayload,
  type BrandsFilter,
} from './model';

type BrandsClientOptions = {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
};

export function createBrandsClient({ config, accessToken, fetcher }: BrandsClientOptions) {
  const api = createShigoApiClient(
    { ...config, apiBaseUrl: config.apiBaseUrl },
    accessToken,
    fetcher,
  );

  return {
    list(filter: BrandsFilter) {
      return api.request({
        path: `/brands?${serializeBrandsFilter(filter).toString()}`,
        schema: brandsPageSchema,
      });
    },
    get(id: string) {
      return api.request({ path: `/brands/${encodeURIComponent(id)}`, schema: brandSchema });
    },
    listIndustries() {
      return api.request({ path: '/brands/industries', schema: z.array(z.string()).default([]) });
    },
    listCountries() {
      return api.request({ path: '/reference/countries', schema: countriesSchema });
    },
    create(payload: BrandPayload, logo?: File | null) {
      return api.request({
        path: '/brands',
        schema: brandSchema,
        init: createBrandMultipartInit('POST', 'createRequest', payload, logo),
      });
    },
    update(id: string, payload: BrandPayload, logo?: File | null) {
      return api.request({
        path: `/brands/${encodeURIComponent(id)}`,
        schema: brandSchema,
        init: createBrandMultipartInit('PUT', 'updateRequest', payload, logo),
      });
    },
  };
}
